import { Link } from "react-router-dom";
import RoosterLogo from "@/components/RoosterLogo";

const COMPANY = "Weathervane, LLC";
const CONTACT_EMAIL = "hello@weathervaneapp.net";
const LAST_UPDATED = "April 2026";

/** Privacy Policy. Drafted to satisfy iOS App Store + Google Play requirements
 *  and to be readable by humans. Review with counsel before public launch. */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-10 lg:py-16">
        <div className="ticker-label mb-4">Last updated · {LAST_UPDATED}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-8" data-testid="privacy-title">
          Privacy Policy
        </h1>

        <Section title="The short version">
          <p>
            We collect the minimum we need to send you accurate forecasts and
            news digests. We don&apos;t sell your data. We don&apos;t track you
            across the web. You can delete your account and all of your data at
            any time from your dashboard.
          </p>
        </Section>

        <Section title="Who we are">
          <p>
            {COMPANY} (&quot;Weathervane,&quot; &quot;we,&quot; &quot;us&quot;)
            operates Weathervane — a weather and news email service available
            on the web at weathervaneapp.net and as iOS/Android applications.
            Reach us at <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="What we collect">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <span className="font-semibold">Account info:</span> first name,
              email address, and a password hash (we never store your raw
              password). For social sign-in we receive your name, email, and
              the provider&apos;s subject identifier.
            </li>
            <li>
              <span className="font-semibold">Subscription preferences:</span>
              {" "}forecast locations (latitude/longitude or address you
              provided), delivery time, and news topics you select.
            </li>
            <li>
              <span className="font-semibold">Device data (apps only):</span>
              {" "}push notification tokens, app version, OS version. Used
              solely to deliver the notifications you opted in to.
            </li>
            <li>
              <span className="font-semibold">Operational logs:</span> IP
              addresses (kept ≤ 24 hours for brute-force protection), email
              delivery status, and error traces.
            </li>
          </ul>
        </Section>

        <Section title="What we don&apos;t collect">
          <ul className="list-disc pl-6 space-y-2">
            <li>We don&apos;t use third-party analytics or advertising trackers.</li>
            <li>We don&apos;t collect background or precise device location.</li>
            <li>We don&apos;t sell or rent your information to anyone.</li>
          </ul>
        </Section>

        <Section title="How we use your data">
          <ul className="list-disc pl-6 space-y-2">
            <li>To send the daily forecasts and news digests you subscribed to.</li>
            <li>To deliver storm-warning push notifications when you opt in.</li>
            <li>To authenticate you and prevent abuse of the service.</li>
            <li>To respond to your support requests.</li>
          </ul>
        </Section>

        <Section title="Service providers we share with">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <span className="font-semibold">Resend</span> — email delivery
              (your email address + the daily HTML digest).
            </li>
            <li>
              <span className="font-semibold">Anthropic Claude</span> — AI
              narrative + banter generation. We send forecast/news data and
              your selected city label; we do not send your email or password.
            </li>
            <li>
              <span className="font-semibold">Perplexity Sonar</span> — news
              article retrieval based on your topic preferences.
            </li>
            <li>
              <span className="font-semibold">Open-Meteo, ECMWF, NWS,
              MET Norway</span> — weather model providers. We send only
              latitude and longitude.
            </li>
            <li>
              <span className="font-semibold">Google / Apple</span> — only when
              you choose to sign in with them.
            </li>
            <li>
              <span className="font-semibold">Apple APNs / Firebase Cloud
              Messaging</span> — push notification routing for the iOS and
              Android apps.
            </li>
          </ul>
        </Section>

        <Section title="Your rights">
          <p>
            You can access, edit, or delete your account and all associated
            data at any time from your dashboard. You can unsubscribe from any
            email by clicking the &quot;Unsubscribe&quot; link in the footer or
            by emailing <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <p className="mt-3">
            If you reside in the EU, UK, or California, you have additional
            rights to access, correct, port, or erase your data. Contact us and
            we&apos;ll respond within 30 days.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            Account and subscription data is retained until you delete your
            account. Email delivery logs are kept for 90 days. Login attempt
            records auto-delete after 24 hours. Backups are rotated every 30
            days.
          </p>
        </Section>

        <Section title="Children">
          <p>
            Weathervane is not directed at children under 13 and we do not
            knowingly collect data from them. If you believe we have, contact
            us and we&apos;ll delete it.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            We&apos;ll email you and update this page if we materially change
            how we handle your data. Continued use after a change means you
            accept the updated policy.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions or requests? Email <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-black/10 text-sm text-neutral-700">
          <Link to="/" className="underline">← Back to Weathervane</Link>
          <span className="mx-3 text-neutral-300">|</span>
          <Link to="/terms" className="underline">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/?stay=1" className="flex items-center gap-3">
          <RoosterLogo size={36} />
          <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
        </Link>
      </div>
    </header>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-9">
      <h2 className="font-display text-2xl font-black tracking-tighter mb-3">{title}</h2>
      <div className="text-[15px] text-neutral-800 leading-relaxed">{children}</div>
    </section>
  );
}
